import { z } from "zod";
import { sortSchema } from "./pagination";

/**
 * HCA Moravia — veřejný kontaktní formulář (public website).
 *
 * Anti-spam vrstvy přímo ve schématu:
 *   - `honeypot`: skryté pole, lidé ho nevyplní, boti ano
 *   - `renderedAt`: timestamp (ms) kdy se formulář vyrendroval; server odmítne
 *     submissions rychlejší než 2 s
 *
 * Server navíc aplikuje per-IP rate limit (v routeru). Captcha vrstva
 * (Turnstile) může přijít později — schéma na to nemá vliv.
 *
 * Forma předává všechna textová pole jako stringy (RHF + Input má rád
 * stringy, ne `unknown`). Prázdné optional řetězce se normalizují na
 * `undefined` na konci přes `.transform()`. Vstup formuláře = `z.input`,
 * payload do API = `z.output`/`z.infer`.
 */

const emptyToUndefined = <T extends string | undefined>(v: T): T | undefined =>
  v === undefined || v === "" ? undefined : v;

export const submitKontaktSchema = z
  .object({
    email: z
      .string()
      .trim()
      .toLowerCase()
      .min(1, "E-mail je povinný")
      .email("Neplatný e-mail")
      .max(255, "E-mail musí být 255 znaků nebo méně"),
    jmeno: z
      .string()
      .trim()
      .max(255, "Jméno musí být 255 znaků nebo méně")
      .optional()
      .transform(emptyToUndefined),
    telefon: z
      .string()
      .trim()
      .max(32, "Telefon musí být 32 znaků nebo méně")
      .optional()
      .transform(emptyToUndefined)
      .refine(
        (v) => v === undefined || /^[+\d(][\d\s\-()/]*$/.test(v),
        "Telefon obsahuje neplatné znaky",
      )
      .refine(
        (v) => v === undefined || (v.match(/\d/g)?.length ?? 0) >= 6,
        "Telefon musí mít alespoň 6 číslic",
      ),
    zprava: z
      .string()
      .trim()
      .max(2000, "Zpráva musí být 2000 znaků nebo méně")
      .optional()
      .transform(emptyToUndefined),
    consent: z.literal(true, {
      errorMap: () => ({ message: "Souhlas se zpracováním je povinný" }),
    }),
    // Anti-spam: honeypot je vždy prázdný string pro lidi.
    honeypot: z.string().max(255).optional(),
    // Anti-spam: kdy se form mountnul (ms epoch). Server kontroluje minimum 2 s.
    renderedAt: z.number().int().positive("Chybí časové razítko"),
    // Cloudflare Turnstile token. Server ho ověří, pokud je nakonfigurován
    // TURNSTILE_SECRET_KEY; bez konfigurace se přeskakuje (dev).
    turnstileToken: z.string().max(4096, "Neplatný token ověření").optional(),
  })
  .strict();

/** Output (po Zod parseru) — používá API. */
export type SubmitKontaktInput = z.infer<typeof submitKontaktSchema>;
/** Input (před parserem) — používá formulář. */
export type SubmitKontaktFormValues = z.input<typeof submitKontaktSchema>;

/** Whitelist řazení zpráv z webu — router mapuje klíče na sloupce. */
export const KONTAKT_ZPRAVY_SORT_KEYS = ["datum", "stav"] as const;
export type KontaktZpravySortKey = (typeof KONTAKT_ZPRAVY_SORT_KEYS)[number];

/** Admin výpis zpráv z kontaktního formuláře (stránkovaný, filtr nevyřízené). */
export const listKontaktZpravSchema = z
  .object({
    page: z.number().int().min(1).default(1),
    pageSize: z.number().int().min(1).max(100).default(20),
    /** True = jen zprávy bez `resolvedAt`. */
    onlyUnresolved: z.boolean().default(false),
  })
  .merge(sortSchema(KONTAKT_ZPRAVY_SORT_KEYS, "desc"));

export type ListKontaktZpravInput = z.infer<typeof listKontaktZpravSchema>;

/** Přepnutí stavu vyřízení zprávy adminem. */
export const setKontaktZpravaResolvedSchema = z.object({
  id: z.string().uuid("Neplatné ID zprávy"),
  resolved: z.boolean(),
});

export type SetKontaktZpravaResolvedInput = z.infer<typeof setKontaktZpravaResolvedSchema>;
